<crowdctrl-unauthenticated-page [pageTitle]="pageTitle" [showFooter]="false" defaultHref="/login">
  <div class="about-project">
    <crowdctrl-unauth-page-intro>
      <h1 crowdctrlUnauthIntroTitle>About</h1>
      <p crowdctrlUnauthIntroLead>{{ appSubtitle }}</p>
      <p crowdctrlUnauthIntroExtra class="about-project__text">
        CrowdCTRL is an AI and data-driven initiative informed by teaching practice, including
        high-demand formats such as lectorials, where many students need help at once and informal
        signalling for assistance is easy to overwhelm.
      </p>
    </crowdctrl-unauth-page-intro>

    <section class="about-project__section" aria-labelledby="about-why-exists">
      <h2 id="about-why-exists" class="about-project__heading">Why CrowdCTRL exists</h2>
      <p class="about-project__text">
        Teaching in university settings makes informal help-seeking hard to run fairly. In smaller
        classes with a single teacher, tracking who needs support competes with explanation,
        questioning, and classroom flow. Quiet or hesitant students are less likely to push to the
        front of an informal queue, and mistakes about turn order are easy to read as favouritism or
        dismissal.
      </p>
      <p class="about-project__text">
        In large, high-demand formats such as lectorials, the same dynamics scale up. Students may
        cluster physically near staff, raise hands across the room, or leave their seats to improve
        visibility. That rewards proximity and assertiveness more than need, pulls attention away
        from the task, and makes it difficult for a team of tutors to share a single, legible
        picture of who is waiting and in what order.
      </p>
      <p class="about-project__text">
        CrowdCTRL responds to that kind of classroom reality. It is designed to make demand visible,
        keep ordering explicit, and encode rules so allocation is easier to explain, audit, and
        improve for staff and students alike.
      </p>
    </section>

    <section class="about-project__section" aria-labelledby="about-purpose">
      <h2 id="about-purpose" class="about-project__heading">What this is</h2>
      <p class="about-project__text">
        This app helps teams run queues in busy teaching and lab sessions: fair ordering, clear
        status, and shared visibility into who is waiting and who is being helped. That makes it
        easier to keep the room organised when demand is high.
      </p>
    </section>

    <section class="about-project__section" aria-labelledby="about-fairness">
      <h2 id="about-fairness" class="about-project__heading">
        Fairness and transparent allocation
      </h2>
      <p class="about-project__text">
        In those environments, informal help requests often reward assertiveness rather than need,
        which can feel unfair and add stress for both students and staff.
      </p>
      <p class="about-project__text">
        CrowdCTRL is designed to improve fairness and reduce that pressure. It replaces informal
        ways of deciding who gets help with transparent, policy-driven allocation so people are
        served using clear, equitable rules, and staff time is used more effectively.
      </p>
    </section>

    <section
      class="about-project__section about-project__tiles-section"
      aria-labelledby="about-explore"
    >
      <h2 id="about-explore" class="about-project__heading">Learn more</h2>
      <nav class="dashboard-tiles" aria-label="Learn more topics">
        <crowdctrl-service-nav-tile route="/about/features" label="Features" icon="grid-outline" />
        <crowdctrl-service-nav-tile
          route="/about/how-it-works"
          label="How it works"
          icon="map-outline"
        />
        <crowdctrl-service-nav-tile
          route="/about/technical"
          label="Technical"
          icon="code-slash-outline"
        />
        <crowdctrl-service-nav-tile
          route="/about/future-directions"
          label="Future directions"
          icon="rocket-outline"
        />
      </nav>
    </section>

    <section
      class="about-project__section about-project__tiles-section about-project__tiles-section--get-started"
      aria-labelledby="about-signin"
    >
      <h2 id="about-signin" class="about-project__heading">Get started</h2>
      <nav class="dashboard-tiles" aria-label="Get started">
        <crowdctrl-service-nav-tile
          themePrimary
          route="/register-interest"
          label="Register interest"
          icon="person-add-outline"
        />
      </nav>
      <p class="about-project__text">
        Use Register interest if you want us to follow up on access. If your organisation is
        approved, your <strong>team lead</strong> is provisioned with a tenancy they can manage: set
        up customised workspaces and register the people who will use them.
      </p>
      <p class="about-project__text">
        When your team lead has added you, sign in from the login page with the account your team
        lead registered for you. For questions, use the contact options there or ask your team lead.
      </p>
      <a
        href="/login"
        class="about-project__signin-link"
        (click)="unauthNav.forward($event, '/login')"
        >Sign in</a
      >
    </section>
  </div>
</crowdctrl-unauthenticated-page>
